import React, {
  Children,
  PropsWithChildren,
  ReactElement,
  SetStateAction,
  useState,
} from 'react';

import { onTextChange } from 'helper/controlledField';
import { DropdownButton } from 'react-bootstrap';
import SubtitleRegular from 'components/Typography/SubtitleRegular';
import TextField from 'components/Typography/TextField';
import Icon from 'components/DesignSystem/Icon';
import Button from 'components/DesignSystem/Button';
import CheckboxSelector from './CheckboxSelector';
import RadioSelector from './RadioSelector';

import './index.scss';

export function setRadioValue(
  setter: React.Dispatch<React.SetStateAction<string | number>>,
  ref: React.RefObject<HTMLDivElement | null>,
) {
  return (v: SetStateAction<string | number>) => {
    const dropdownButton = ref.current?.querySelector('.dropdown-toggle') as HTMLButtonElement;
    setter(v);
    dropdownButton.click();
  };
}

interface SelectorProps<T> {
  label?: string;
  id?: string;
  className?: string;
  containerClassName?: string;
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
  disabled?: boolean;
  validated?: boolean;
  isValid?: boolean;
  hint?: string;
  error?: string;
  search?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  selectAllLabel?: string;
  selectedLabel?: string;
  unselectedLabel?: string;
  asBlock?: boolean;
  fixedTitle?: React.ReactNode;
  clearOptionsButton?: boolean;
  clearOptionsLabel?: string;
  itemContainerMaxHeight?: string | number;
}

export interface SelectorItemProps {
  label: string;
  value: string;
}

export default function Selector<T>({
  children,
  label = undefined,
  id,
  className = '',
  containerClassName = '',
  value,
  setValue,
  disabled = false,
  validated = false,
  isValid = true,
  hint = undefined,
  error = undefined,
  search = false,
  placeholder = 'Select option',
  searchPlaceholder = 'Search options',
  selectAllLabel = 'Select All',
  selectedLabel = 'Selected',
  unselectedLabel = 'Unselected',
  asBlock = false,
  fixedTitle = undefined,
  clearOptionsButton = false,
  clearOptionsLabel = 'Excluir opções',
  itemContainerMaxHeight,
}: PropsWithChildren<SelectorProps<T>>) {
  const dropdownDivRef = React.useRef<HTMLDivElement>(null);
  const isMultiSelect = Array.isArray(value);
  const emptyValue = (isMultiSelect ? [] : undefined) as unknown as T;
  const [searchTerm, setSearchTerm] = useState('');
  const normalizedSearchTerm = searchTerm.toLowerCase();
  const optionElements = Children.toArray(children) as ReactElement<any>[];
  const options: SelectorItemProps[] = optionElements.map((option) => ({
    value: option.props.value,
    label: option.props.children as string,
  }));
  const filteredItems = searchTerm
    ? options.filter((o) => o.label.toLowerCase().includes(normalizedSearchTerm))
    : options;
  const title = isMultiSelect
    ? options.filter((o) => value.includes(o.value)).map((o) => o.label).join(', ')
    : options.find((o) => o.value === value)?.label;
  const filled = isMultiSelect
    ? value.length > 0
    : value !== emptyValue;
  const showError = error && validated && !isValid;
  const clearValue = () => {
    setValue(emptyValue);
    setSearchTerm('');
  };

  return (
    <div className={`ds-selector ${containerClassName} ${disabled ? 'disabled' : ''}`}>
      {label && <SubtitleRegular>{label}</SubtitleRegular>}
      <div className="ds-selector-dropdown-button-container">
        <DropdownButton
          ref={dropdownDivRef}
          id={id}
          title={fixedTitle || title || placeholder}
          disabled={disabled}
          className={`
            ds-selector-dropdown-button ${className}
            ${showError ? 'error' : ''} ${filled ? 'filled' : ''} ${asBlock ? 'as-block' : ''}
          `}
        >
          {search && (
            <>
              <div className="ds-selector-search">
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  className="ds-selector-search-text"
                  value={searchTerm}
                  onChange={onTextChange(setSearchTerm)}
                />
                <Icon iconType="search" className="search-icon" />
              </div>
              <hr />
            </>
          )}
          <div
            className="ds-selector-item-container"
            style={
              itemContainerMaxHeight != null ? { maxHeight: itemContainerMaxHeight } : undefined
            }
          >
            {clearOptionsButton && filled && (
              <Button
                onClick={clearValue}
                icon="close"
                iconPlacement="trailing"
                className="ds-selector-clear-options-button"
              >
                {clearOptionsLabel}
              </Button>
            )}
            {isMultiSelect && (
              <CheckboxSelector
                itemCount={options.length}
                filteredItems={filteredItems}
                selectedItems={value}
                setValue={setValue as React.Dispatch<React.SetStateAction<readonly string[]>>}
                searchTerm={searchTerm}
                selectAllLabel={selectAllLabel}
                selectedLabel={selectedLabel}
                unselectedLabel={unselectedLabel}
              />
            )}
            {!isMultiSelect && (
              <RadioSelector
                filteredItems={filteredItems}
                value={value as string | number}
                setValue={setRadioValue(
                  setValue as React.Dispatch<React.SetStateAction<string | number>>,
                  dropdownDivRef,
                )}
              />
            )}
          </div>
        </DropdownButton>
        <div className="ds-selector-dropdown-button-icons">
          <Button onClick={clearValue} icon="close" type="tertiary" className="ds-selector-close-button" />
          <Icon className="ds-selector-dropdown-arrow" iconType="down_arrow" />
        </div>
      </div>
      {showError && (
        <div className="ds-selector-error-label">
          <Icon iconType="warning" size="small" />
          <TextField>{error}</TextField>
        </div>
      )}
      {hint && !showError && (
        <div className="ds-selector-hint-label">
          <Icon iconType="info" size="small" />
          <TextField>{hint}</TextField>
        </div>
      )}
    </div>
  );
}
